import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { getModulesGuards, getTreeConfig } from './configs';
import { PolicyPanelManager } from '../PolicyPanelManager';
import { Feature } from '@nirvana/shared/constants';
import { ListItem } from '../../../../ui/gzSingleSelectList/types';
import { EnumListItem } from '../../../../ui/gzEnumList/types';
import { getModuleLabelById, getSectionLabelById } from '../searchableComponents/searchablePolicyPanel/utils/treeItems';

export const INHERITABLE_MODULES: ModuleId[] = [
  ModuleId.GENERAL,
  ModuleId.RELAY,
  ModuleId.ANTIMALWARE,
  ModuleId.SANDBOX,
  ModuleId.FIREWALL,
  ModuleId.CONTENT_CONTROL,
  ModuleId.PATCH_MANAGEMENT,
  ModuleId.APPLICATION_CONTROL,
  ModuleId.DEVICE_CONTROL,
  ModuleId.INTEGRITY_MONITOR,
  ModuleId.EXCHANGE_PROTECTION,
  ModuleId.ENCRYPTION,
  ModuleId.EDR_SENSOR,
  ModuleId.STORAGE_PROTECTION,
  ModuleId.INDICATORS_OF_RISK,
  ModuleId.BLOCKLIST_RULES,
];

export const INHERITABLE_SECTIONS = {
  [ModuleId.GENERAL]: {
    canSelectAll: true,
    sections: [
      SectionId.GENERAL_NOTIFICATIONS,
      SectionId.SETTINGS,
      SectionId.COMMUNICATION,
      SectionId.UPDATE,
      SectionId.SECURITY_TELEMETRY,
    ],
  },
  [ModuleId.ANTIMALWARE]: {
    canSelectAll: true,
    sections: [
      SectionId.ANTIMALWARE_ON_ACCESS,
      SectionId.ANTIMALWARE_ON_EXECUTE,
      SectionId.ANTIMALWARE_ON_DEMAND,
      SectionId.ANTIMALWARE_ANTI_TAMPERING,
      SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE,
      SectionId.ANTIMALWARE_ANTI_EXPLOIT,
      SectionId.ANTIMALWARE_SECURITY_SERVERS,
      SectionId.SETTINGS,
      SectionId.EXCLUSIONS,
    ],
  },
  [ModuleId.FIREWALL]: {
    canSelectAll: true,
    sections: [SectionId.SETTINGS, SectionId.FIREWALL_NETWORK, SectionId.FIREWALL_ADVANCED],
  },
  [ModuleId.CONTENT_CONTROL]: {
    canSelectAll: true,
    sections: [
      SectionId.CONTENT_CONTROL_TRAFFIC,
      SectionId.CONTENT_CONTROL_WEB,
      SectionId.CONTENT_CONTROL_DATA_PROTECTION,
      SectionId.CONTENT_CONTROL_NETWORK_MONITOR,
    ],
  },
  [ModuleId.APPLICATION_CONTROL]: {
    canSelectAll: true,
    sections: [],
  },
  [ModuleId.DEVICE_CONTROL]: {
    canSelectAll: true,
    sections: [SectionId.RULES, SectionId.EXCLUSIONS],
  },
  [ModuleId.INTEGRITY_MONITOR]: {
    canSelectAll: false,
    sections: [SectionId.INTEGRITY_MONITOR_REAL_TIME],
  },
  [ModuleId.RELAY]: {
    canSelectAll: true,
    sections: [SectionId.COMMUNICATION, SectionId.UPDATE],
  },
  [ModuleId.EXCHANGE_PROTECTION]: {
    canSelectAll: true,
    sections: [
      SectionId.GENERAL,
      SectionId.EXCHANGE_ANTIMALWARE,
      SectionId.EXCHANGE_ANTISPAM,
      SectionId.EXCHANGE_CONTENT_CONTROL,
    ],
  },
  [ModuleId.SANDBOX]: {
    canSelectAll: false,
    sections: [SectionId.ENDPOINT_SENSOR, SectionId.NETWORK_SENSOR, SectionId.ICAP_SENSOR, SectionId.SANDBOX_MANAGER],
  },
  [ModuleId.ENCRYPTION]: {
    canSelectAll: false,
    sections: [SectionId.GENERAL],
  },
  [ModuleId.EDR_SENSOR]: {
    canSelectAll: false,
    sections: [SectionId.GENERAL],
  },
  [ModuleId.PATCH_MANAGEMENT]: {
    canSelectAll: true,
    sections: [],
  },
  [ModuleId.STORAGE_PROTECTION]: {
    canSelectAll: false,
    sections: [SectionId.STORAGE_PROTECTION_ICAP, SectionId.EXCLUSIONS],
  },
  [ModuleId.INDICATORS_OF_RISK]: {
    canSelectAll: true,
    sections: [],
  },
  [ModuleId.BLOCKLIST_RULES]: {
    canSelectAll: true,
    sections: [],
  },
};

export function getAvailableModuleIdsForInheritance(): ModuleId[] {
  const modulesGuards = getModulesGuards();

  return INHERITABLE_MODULES.filter((moduleId: ModuleId) => {
    // special case for sandbox
    if (moduleId === ModuleId.SANDBOX) {
      return (
        PolicyPanelManager.getInstance().isSandboxEndpointSensorSectionAvailable() ||
        Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SANDBOX_LOCAL_VISIBLE])
      );
    }

    // apply visibility guard
    return modulesGuards[moduleId]();
  });
}
export function getAvailableModulesForInheritance(): ListItem[] {
  return getAvailableModuleIdsForInheritance().map(moduleId => {
    return {
      label: getModuleLabelById(moduleId),
      value: moduleId,
    };
  });
}

export function getAvailableSectionsForInheritanceByModule(moduleId: ModuleId): {
  canSelectAll: boolean;
  filterOptions: EnumListItem[];
} {
  // module is not inheritable
  if (!INHERITABLE_MODULES.includes(moduleId)) {
    return {
      canSelectAll: true,
      filterOptions: [],
    };
  }

  const moduleConfig = getTreeConfig()[moduleId];
  const inheritableSectionsConfig: { canSelectAll: boolean; sections: SectionId[] } = INHERITABLE_SECTIONS[moduleId];

  return {
    canSelectAll: inheritableSectionsConfig.canSelectAll,
    filterOptions: inheritableSectionsConfig.sections
      .filter(sectionId => moduleConfig[sectionId].guard()) // apply visibility guard
      .map(sectionId => {
        // map to EnumListItem
        return {
          label: getSectionLabelById(sectionId, moduleId),
          value: sectionId,
          selected: false,
        };
      }),
  };
}
